import React, { useEffect, useRef, useState } from "react";
import { Seo } from "@/components/Seo";
import { Link, useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle2, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Phase 14 — landing page for the emailed verification link
// (/verify-email?token=…). Verification is SOFT during F&F: the account works
// either way, this just confirms inbox ownership and clears the nudge banner.
export default function VerifyEmail() {
  const [params] = useSearchParams();
  const { user, refresh } = useAuth();
  const [state, setState] = useState("verifying"); // verifying | success | failed
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return; // StrictMode double-mount guard — token is single-use
    ran.current = true;
    const token = params.get("token") || "";
    if (!token) { setState("failed"); return; }
    api.verifyEmail(token)
      .then(async () => {
        setState("success");
        if (user) await refresh();
      })
      .catch(() => setState("failed"));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- intentional single-shot on mount

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-6 py-16">
      <Seo title="Verify email" />
      <Card className="w-full max-w-md border-neutral-200">
        <CardContent className="p-10 text-center">
          {state === "verifying" && (
            <div data-testid="verify-email-pending" className="text-neutral-500">Verifying your email…</div>
          )}
          {state === "success" && (
            <div data-testid="verify-email-success">
              <CheckCircle2 className="w-12 h-12 text-forest mx-auto" />
              <div className="font-display text-3xl text-forest tracking-tight mt-4">Email verified</div>
              <p className="text-neutral-600 mt-2 text-sm">Thanks — you're all set.</p>
              <Link
                to={user ? "/dashboard" : "/login"}
                data-testid="verify-email-continue-link"
                className="inline-block mt-6 px-8 py-3 rounded-full bg-forest hover:bg-forest-hover text-white text-sm font-semibold transition-colors"
              >
                {user ? "Back to my dashboard" : "Sign in"}
              </Link>
            </div>
          )}
          {state === "failed" && (
            <div data-testid="verify-email-failed">
              <XCircle className="w-12 h-12 text-terracotta mx-auto" />
              <div className="font-display text-3xl text-forest tracking-tight mt-4">Link expired</div>
              <p className="text-neutral-600 mt-2 text-sm">
                This verification link is invalid or has already been used. You can request a
                fresh one from the banner shown when you're signed in.
              </p>
              <Link to="/" className="inline-block mt-6 text-sm text-forest underline underline-offset-2">Back home</Link>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
